import { useState } from 'react';
import type { Lang } from '../data/types';
import { useDashboard, useDashboardActions } from '../data/context';
import { Icon } from '../components/Icon';
import { Switch } from '../components/Switch';

/** Gestione della sala: zone + tavoli (#39). Il titolare configura i tavoli che
 *  vede poi nel calendario (aggiungi/rinomina/cambia posti/elimina). */
export function TablesView({ lang }: { lang: Lang }) {
  const data = useDashboard();
  const actions = useDashboardActions();
  const T = (it: string, en: string) => (lang === 'it' ? it : en);

  const [busy, setBusy] = useState(false);
  const [newZone, setNewZone] = useState('');
  const [addTo, setAddTo] = useState<string | null>(null);
  const [tLabel, setTLabel] = useState('');
  const [tSeats, setTSeats] = useState('2');

  const run = (fn: () => Promise<void>) => {
    setBusy(true);
    fn()
      .catch((e) => alert(T('Operazione non riuscita', 'Action failed') + ': ' + (e instanceof Error ? e.message : String(e))))
      .finally(() => setBusy(false));
  };

  const zones = [...data.zones].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  return (
    <div className="panel">
      <div className="block-head">
        <h2>{T('Tavoli', 'Tables')}</h2>
        <p className="sub">
          {T(
            'La tua sala: le zone e i tavoli che vedi nel calendario. Aggiungi, rinomina, cambia i posti.',
            'Your room: the zones and tables shown in the calendar. Add, rename, change seats.',
          )}
        </p>
      </div>

      {zones.length === 0 && <p className="sub">{T('Nessuna zona ancora — aggiungine una qui sotto.', 'No zones yet — add one below.')}</p>}

      {zones.map((z) => (
        <div className="svc-card" key={z.dbId} style={{ marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <input
              className="input"
              defaultValue={z.name[lang] || z.name.it}
              aria-label={T('Nome zona', 'Zone name')}
              style={{ fontWeight: 600, maxWidth: 220 }}
              onBlur={(e) => {
                const v = e.target.value.trim();
                if (v && v !== (z.name[lang] || z.name.it)) run(() => actions.updateZone(z.dbId!, { nameIt: v }));
              }}
            />
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
              <Switch
                on={z.bookableOnline ?? true}
                ariaLabel={T('Prenotabile online', 'Bookable online')}
                onToggle={() => run(() => actions.updateZone(z.dbId!, { bookableOnline: !(z.bookableOnline ?? true) }))}
              />
              {T('Prenotabile online', 'Bookable online')}
            </label>
            <span className="grow" />
            <button
              className="btn btn-ghost btn-sm"
              disabled={busy}
              onClick={() => {
                if (confirm(T('Eliminare la zona e tutti i suoi tavoli?', 'Delete the zone and all its tables?')))
                  run(() => actions.deleteZone(z.dbId!));
              }}
            >
              {T('Elimina zona', 'Delete zone')}
            </button>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
            {data.tables
              .filter((t) => t.zone === z.id)
              .map((tb) => (
                <div
                  key={tb.dbId}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, border: '1px solid var(--rule, rgba(26,22,18,0.14))', borderRadius: 10, padding: '6px 8px' }}
                >
                  <input
                    className="input"
                    defaultValue={tb.id}
                    aria-label={T('Nome tavolo', 'Table label')}
                    style={{ width: 64, fontWeight: 600, padding: '4px 6px' }}
                    onBlur={(e) => {
                      const v = e.target.value.trim();
                      if (v && v !== tb.id) run(() => actions.updateTable(tb.dbId!, { label: v }));
                    }}
                  />
                  <Icon name="users" size={14} />
                  <input
                    className="input tnum"
                    type="number"
                    min={1}
                    max={50}
                    defaultValue={tb.seats}
                    aria-label={T('Posti', 'Seats')}
                    style={{ width: 56, padding: '4px 6px' }}
                    onBlur={(e) => {
                      const v = Number(e.target.value);
                      if (v >= 1 && v !== tb.seats) run(() => actions.updateTable(tb.dbId!, { seats: v }));
                    }}
                  />
                  <button
                    className="btn btn-ghost btn-sm"
                    disabled={busy}
                    aria-label={T('Elimina tavolo', 'Delete table')}
                    style={{ padding: '4px 8px' }}
                    onClick={() => run(() => actions.deleteTable(tb.dbId!))}
                  >
                    ✕
                  </button>
                </div>
              ))}
          </div>

          {addTo === z.dbId ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <input className="input" placeholder={T('Nome (es. T7)', 'Label (e.g. T7)')} value={tLabel} onChange={(e) => setTLabel(e.target.value)} autoFocus style={{ maxWidth: 140 }} />
              <input className="input tnum" type="number" min={1} max={50} value={tSeats} onChange={(e) => setTSeats(e.target.value)} style={{ width: 80 }} aria-label={T('Posti', 'Seats')} />
              <button
                className="btn btn-primary btn-sm"
                disabled={busy || !tLabel.trim()}
                onClick={() =>
                  run(async () => {
                    await actions.createTable({ zoneId: z.dbId!, label: tLabel.trim(), seats: Number(tSeats) || 2 });
                    setTLabel('');
                    setTSeats('2');
                    setAddTo(null);
                  })
                }
              >
                {T('Aggiungi', 'Add')}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => setAddTo(null)}>{T('Annulla', 'Cancel')}</button>
            </div>
          ) : (
            <button className="btn btn-ghost btn-sm" style={{ marginTop: 12 }} onClick={() => { setAddTo(z.dbId!); setTLabel(''); setTSeats('2'); }}>
              + {T('Aggiungi tavolo', 'Add table')}
            </button>
          )}
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <input className="input" placeholder={T('Nuova zona (es. Dehors)', 'New zone (e.g. Terrace)')} value={newZone} onChange={(e) => setNewZone(e.target.value)} style={{ maxWidth: 220 }} />
        <button
          className="btn btn-primary btn-sm"
          disabled={busy || !newZone.trim()}
          onClick={() => run(async () => { await actions.createZone({ nameIt: newZone.trim() }); setNewZone(''); })}
        >
          + {T('Aggiungi zona', 'Add zone')}
        </button>
      </div>
    </div>
  );
}
